import { Koi } from '@ksyos/koi';
import type { TypedReadSchema } from '@common/formEngine/typedReadSchema.types';
import type { ProcessActivityAsset } from '@common/validation/processActivityAsset';
import { getProcessActivityAssetsSchema } from '@common/validation/processActivityAsset';
import { getTextAreaStringSchema } from '@common/validation/utils/getTextAreaStringSchema';

export enum OsasCloudPatExaminationStatus {
    Mislukt = 'Mislukt',
    Successvol = 'Succesvol',
}

export type WatchPatSleepStudyPdfData = {
    AHICentral?: string | number | null;
    odi3Percent?: string | number | null;
    odi4Percent?: string | number | null;
    oxygenSaturationMeanValue?: string | number | null;
    oxygenSaturationMinValue?: string | number | null;
    pAhi3Percent?: string | number | null;
    satBelow90InSeconds?: string | number | null;
    totalSleepTimeInSeconds?: string | number | null;
};

export type OsasCloudPatExaminationCustomControlData = WatchPatSleepStudyPdfData & {
    assets: ProcessActivityAsset[];
    hasWarning: boolean | null;
    isConsultUrgent?: boolean;
    isFailed: boolean;
    remark?: string | null;
    status?: OsasCloudPatExaminationStatus;
    studyIds: string[];
};

export const watchPatSleepStudyPdfDataSchema = Koi.object({
    AHICentral: Koi.alternatives().try(Koi.string(), Koi.number()).allow(null),
    odi3Percent: Koi.alternatives().try(Koi.string(), Koi.number()).allow(null),
    odi4Percent: Koi.alternatives().try(Koi.string(), Koi.number()).allow(null),
    oxygenSaturationMeanValue: Koi.alternatives().try(Koi.string(), Koi.number()).allow(null),
    oxygenSaturationMinValue: Koi.alternatives().try(Koi.string(), Koi.number()).allow(null),
    pAhi3Percent: Koi.alternatives().try(Koi.string(), Koi.number()).allow(null),
    satBelow90InSeconds: Koi.alternatives().try(Koi.string(), Koi.number()).allow(null),
    totalSleepTimeInSeconds: Koi.alternatives().try(Koi.string(), Koi.number()).allow(null),
});

export const watchPatSleepStudyPdfDataConsultSchema = Koi.object({
    odi3Percent: Koi.string().allow(null),
    odi4Percent: Koi.string().allow(null),
    oxygenSaturationMeanValue: Koi.string().allow(null),
    oxygenSaturationMinValue: Koi.string().allow(null),
    pAhi3Percent: Koi.string().allow(null),
});

export const osasCloudPatExaminationSchema = Koi.object({
    assets: getProcessActivityAssetsSchema({ max: 20 }),
    hasWarning: Koi.boolean().allow(null),
    isConsultUrgent: Koi.boolean().optional(),
    isFailed: Koi.boolean(),
    remark: getTextAreaStringSchema(true, 255).optional(),
    status: Koi.string()
        .valid(...Object.values(OsasCloudPatExaminationStatus))
        .optional(),
    studyIds: Koi.array().items(Koi.string()),
}).concat(watchPatSleepStudyPdfDataSchema);

type OsasCloudPatExaminationReadKey =
    | keyof OsasCloudPatExaminationCustomControlData
    | 'assets'
    | 'customcontroldata'
    | 'Bijlage';

type OsasCloudPatExaminationReadLabel = 'Bijlage' | 'Toelichting';

export const osasCloudPatExaminationReadSchema: TypedReadSchema<
    OsasCloudPatExaminationReadKey,
    OsasCloudPatExaminationReadLabel
>[] = [
    {
        items: [
            { key: 'assets', label: 'Bijlage' },
            { key: 'customcontroldata', label: 'Bijlage' },
            { key: 'remark', label: 'Toelichting' },
        ],
    },
];
